import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { ArrowLeft, BarChart3, AlertTriangle, Shield, DollarSign, Clock, Users, RefreshCw, TrendingUp, XCircle } from 'lucide-react';

function KpiCard({ label, value, icon: Icon, sub, unavailable }) {
  return (
    <Card className="border" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-3 flex items-center gap-3">
        {Icon && <div className="p-2 rounded-lg bg-muted/40"><Icon className="h-4 w-4 text-muted-foreground" /></div>}
        <div className="min-w-0">
          <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</div>
          {unavailable ? <div className="text-xs text-amber-600 italic">Not yet available</div>
            : <div className="text-lg font-bold leading-tight">{value}</div>}
          {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
        </div>
      </CardContent>
    </Card>
  );
}

function SectionPanel({ title, icon: Icon, loading, error, children }) {
  if (loading) return <Card className="border"><CardHeader className="py-2 px-4"><CardTitle className="text-sm flex items-center gap-2">{Icon && <Icon className="h-4 w-4" />}{title}</CardTitle></CardHeader><CardContent className="p-4"><Skeleton className="h-24 w-full" /></CardContent></Card>;
  if (error) return <Card className="border"><CardHeader className="py-2 px-4"><CardTitle className="text-sm">{title}</CardTitle></CardHeader><CardContent className="p-4 text-xs text-red-500">{error}</CardContent></Card>;
  return (
    <Card className="border">
      <CardHeader className="py-2 px-4 border-b"><CardTitle className="text-sm flex items-center gap-2">{Icon && <Icon className="h-4 w-4" />}{title}</CardTitle></CardHeader>
      <CardContent className="p-4">{children}</CardContent>
    </Card>
  );
}

function BarRow({ label, value, max, color = 'bg-blue-500' }) {
  const pct = max > 0 ? Math.round(value / max * 100) : 0;
  return (
    <div className="flex items-center gap-3 py-1">
      <span className="text-xs w-36 shrink-0 truncate">{label}</span>
      <div className="flex-1 bg-muted/30 rounded-full h-4 overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${Math.max(pct, 1)}%` }} />
      </div>
      <span className="text-xs font-mono w-12 text-right">{value}</span>
    </div>
  );
}

function useAnalytics(endpoints) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const results = {};
    const errs = {};
    await Promise.all(Object.entries(endpoints).map(async ([key, ep]) => {
      try { const res = await api.get(ep); results[key] = res.data; }
      catch (e) { errs[key] = e.response?.data?.detail || 'Failed'; }
    }));
    setData(results);
    setErrors(errs);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  return { data, loading, errors, refresh: fetchAll };
}

export default function RevenueAnalyticsPage() {
  const navigate = useNavigate();
  const { data, loading, errors, refresh } = useAnalytics({
    queues: '/revenue/analytics/queues/summary',
    aging: '/revenue/analytics/aging',
    batches: '/revenue/analytics/batches/summary',
    failures: '/revenue/analytics/failures',
    overrides: '/revenue/analytics/governance/overrides',
    policyChanges: '/revenue/analytics/governance/policy-changes',
    suppressions: '/revenue/analytics/governance/suppressions',
    vendorFailures: '/revenue/analytics/governance/vendor-failures',
    writeoff: '/revenue/analytics/financial/writeoff',
    timing: '/revenue/analytics/financial/conversion-timing',
  });

  const q = data.queues?.queues || {};
  const totalCases = data.queues?.total_cases || 0;
  const batchRate = data.batches?.totals?.success_rate;
  const totalOverrides = data.overrides?.total_overrides || 0;
  const totalSupp = data.suppressions?.total_active || 0;
  const totalPolicyChanges = data.policyChanges?.total_changes || 0;
  const woRate = data.writeoff?.writeoff_rate;

  return (
    <div className="flex-1 overflow-auto p-4 space-y-4 max-w-6xl" data-testid="analytics-page">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold tracking-tight flex items-center gap-2"><BarChart3 className="h-5 w-5" />Revenue Analytics</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => navigate('/revenue/analytics/financial')} data-testid="goto-financial"><TrendingUp className="h-3 w-3" />Financial</Button>
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => navigate('/revenue/analytics/governance')} data-testid="goto-governance"><Shield className="h-3 w-3" />Governance</Button>
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => navigate('/revenue/analytics/failures')} data-testid="goto-failures"><AlertTriangle className="h-3 w-3" />Failures</Button>
          <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => navigate('/revenue')}><ArrowLeft className="h-3 w-3" />Workspace</Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={refresh}><RefreshCw className="h-3 w-3" /></Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kpi-cards">
        <KpiCard label="Responsibility Ready" value={q.responsibility_ready || 0} icon={DollarSign} sub={`of ${totalCases} total`} />
        <KpiCard label="Active Suppressions" value={totalSupp} icon={Shield} />
        <KpiCard label="Batch Success" value={batchRate != null ? `${batchRate}%` : '—'} icon={TrendingUp} unavailable={batchRate == null} />
        <KpiCard label="Policy Versions" value={totalPolicyChanges} icon={Clock} />
        <KpiCard label="Manual Overrides" value={totalOverrides} icon={Users} />
        <KpiCard label="Write-Off Rate" value={woRate != null ? `${woRate}%` : undefined} icon={XCircle} unavailable={woRate == null || woRate === undefined} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Queue Distribution */}
        <SectionPanel title="Queue Distribution" icon={DollarSign} loading={loading} error={errors.queues}>
          {data.queues && (() => {
            const maxQ = Math.max(...Object.values(q), 1);
            return (
              <div>
                <BarRow label="Responsibility Ready" value={q.responsibility_ready || 0} max={maxQ} color="bg-emerald-500" />
                <BarRow label="Policy Holds" value={q.responsibility_holds || 0} max={maxQ} color="bg-orange-500" />
                <BarRow label="Statement Ready" value={q.statement_ready || 0} max={maxQ} color="bg-blue-500" />
                <BarRow label="Collections Ready" value={q.collections_ready || 0} max={maxQ} color="bg-amber-500" />
                <BarRow label="Write-Off Ready" value={q.writeoff_ready || 0} max={maxQ} color="bg-red-400" />
              </div>
            );
          })()}
        </SectionPanel>

        {/* Aging */}
        <SectionPanel title="Aging Distribution" icon={Clock} loading={loading} error={errors.aging}>
          {data.aging?.aging && (
            <div className="space-y-3">
              {Object.entries(data.aging.aging).map(([stage, buckets]) => (
                <div key={stage}>
                  <div className="text-xs font-medium mb-1 capitalize">{stage}</div>
                  <div className="flex gap-2">
                    {Object.entries(buckets).map(([bucket, count]) => (
                      <div key={bucket} className="flex-1 text-center p-2 bg-muted/20 rounded">
                        <div className="text-lg font-bold">{count}</div>
                        <div className="text-[10px] text-muted-foreground">{bucket}d</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionPanel>

        {/* Batch Outcomes */}
        <SectionPanel title="Batch Outcomes" icon={TrendingUp} loading={loading} error={errors.batches}>
          {data.batches && (
            <div className="space-y-2">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-2 bg-emerald-50 rounded"><div className="text-lg font-bold text-emerald-700">{data.batches.totals?.success || 0}</div><div className="text-[10px]">Succeeded</div></div>
                <div className="p-2 bg-red-50 rounded"><div className="text-lg font-bold text-red-700">{data.batches.totals?.failed || 0}</div><div className="text-[10px]">Failed</div></div>
                <div className="p-2 bg-muted/20 rounded"><div className="text-lg font-bold">{data.batches.totals?.processed || 0}</div><div className="text-[10px]">Processed</div></div>
              </div>
              <div className="text-xs text-muted-foreground">Statement batches: {data.batches.statement_batches?.total || 0} | Collections batches: {data.batches.collections_batches?.total || 0}</div>
            </div>
          )}
        </SectionPanel>

        {/* Failures */}
        <SectionPanel title="Failure Summary" icon={AlertTriangle} loading={loading} error={errors.failures}>
          {data.failures && (
            <div className="space-y-2">
              {Object.keys(data.failures.clearinghouse || {}).length === 0 && Object.keys(data.failures.delivery || {}).length === 0 && (data.failures.external_handoffs || []).length === 0
                ? <div className="text-xs text-muted-foreground">No failure data available.</div>
                : <>
                  {Object.entries(data.failures.clearinghouse || {}).map(([cls, cnt]) => (
                    <div key={cls} className="flex justify-between text-xs"><span>Clearinghouse: {cls}</span><span className="font-mono text-red-600">{cnt}</span></div>
                  ))}
                  {Object.entries(data.failures.delivery || {}).map(([cls, cnt]) => (
                    <div key={cls} className="flex justify-between text-xs"><span>Delivery: {cls}</span><span className="font-mono text-red-600">{cnt}</span></div>
                  ))}
                  {(data.failures.external_handoffs || []).map((f, i) => (
                    <div key={i} className="flex justify-between text-xs"><span>{f.domain}: {f.failure_class}</span><span className="font-mono text-red-600">{f.count}</span></div>
                  ))}
                </>
              }
            </div>
          )}
        </SectionPanel>

        {/* Governance */}
        <SectionPanel title="Governance Summary" icon={Shield} loading={loading} error={errors.overrides}>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-2 bg-muted/20 rounded text-center"><div className="text-lg font-bold">{totalOverrides}</div><div className="text-[10px]">Manual Overrides</div></div>
            <div className="p-2 bg-muted/20 rounded text-center"><div className="text-lg font-bold">{totalPolicyChanges}</div><div className="text-[10px]">Policy Changes</div></div>
            <div className="p-2 bg-muted/20 rounded text-center"><div className="text-lg font-bold">{totalSupp}</div><div className="text-[10px]">Active Suppressions</div></div>
            <div className="p-2 bg-muted/20 rounded text-center">
              <div className="text-lg font-bold">{(data.vendorFailures?.clearinghouse_failures?.length || 0) + (data.vendorFailures?.delivery_failures?.length || 0) + (data.vendorFailures?.external_handoff_failures?.length || 0)}</div>
              <div className="text-[10px]">Vendor Failure Types</div>
            </div>
          </div>
        </SectionPanel>

        {/* Conversion Timing */}
        <SectionPanel title="Conversion Timing" icon={Clock} loading={loading} error={errors.timing}>
          {data.timing?.conversion_timing && (
            <div className="space-y-1">
              {Object.entries(data.timing.conversion_timing).map(([stage, t]) => (
                <div key={stage} className="flex justify-between text-xs py-1 border-b last:border-0">
                  <span className="capitalize">{stage.replace(/_/g, ' ')}</span>
                  <span className="font-mono">{t.avg_aging_days}d avg ({t.count} cases)</span>
                </div>
              ))}
            </div>
          )}
        </SectionPanel>
      </div>
    </div>
  );
}
